/**
 * The doctor home's Upcoming bookings tab (docs/ux/dr.md, addendum of
 * 10 September 2026; D-0066): the doctor's appointments for seven days from
 * today, in time order, read from the clinic window narrowed to one doctor.
 * The door words (confirmed, consent, questionnaire) and the marks are the
 * clinic's own; every row opens the case hub. Read only: booking, confirming
 * and attendance stay on the clinic and the hub.
 */

import type { ColumnDef } from '@tanstack/react-table';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { say } from '@/app/announcer';
import { useOnline } from '@/app/online';
import type { ClinicRow } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { StatusChip } from '@/components/matter/StatusChip';
import { Legend, LegendItem } from '@/components/shell/Legend';
import { QueueTable } from '@/components/shell/QueueTable';
import { EmptyState, ErrorPanel } from '@/components/shell/states';
import { Button, buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CONSENT_WORD, QUESTIONNAIRE_WORD, addDays, confirmedWord, dayKey, markWords, timeOf, weekKey } from '@/features/clinic/describe';
import { longDate, pluralise } from '@/lib/format';
import { bookingsWindow, isToday, useDoctorBookings } from './useDoctorQueue';

const hubHref = (ref: string) => `/matters/${encodeURIComponent(ref)}`;

const DAY_LONG = new Intl.DateTimeFormat('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });
const DAY_SHORT = new Intl.DateTimeFormat('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });

/** "Today, 10 September" or "Thursday 11 September" for the row's day. */
export function dayWords(iso: string, now: Date = new Date()): string {
  const key = dayKey(iso);
  return isToday(key, now) ? `Today, ${longDate(iso)}` : DAY_LONG.format(new Date(iso));
}

function WhenCell({ row }: { row: ClinicRow }) {
  const today = isToday(dayKey(row.startsAt));
  return (
    <span className="flex flex-col leading-tight">
      <span className={today ? 'font-semibold text-beam-signature' : ''}>{today ? 'Today' : DAY_SHORT.format(new Date(row.startsAt))}</span>
      <span className="tabular text-xs text-beam-text-muted">{timeOf(row.startsAt)}</span>
    </span>
  );
}

function Words({ row }: { row: ClinicRow }) {
  const words = markWords(row);
  if (words.length === 0) return <span className="text-beam-text-muted">None</span>;
  return <span className="flex flex-wrap gap-x-2">{words.map((w) => <span key={w} className={w === 'Urgent' || w.includes('DNA') || w.startsWith('Late') ? 'font-medium text-beam-error' : ''}>{w}</span>)}</span>;
}

function columns(): ColumnDef<ClinicRow, unknown>[] {
  return [
    { id: 'when', accessorKey: 'startsAt', header: 'When', meta: { priority: 1, width: 168 }, cell: ({ row }) => <WhenCell row={row.original} /> },
    { id: 'clinic', accessorFn: (r) => r.clinic.name, header: 'Clinic', meta: { priority: 1, width: 140 }, cell: ({ row }) => <span className="block truncate" title={row.original.clinic.name}>{row.original.clinic.name}</span> },
    { id: 'matter', accessorKey: 'matterRef', header: 'Case', meta: { priority: 1, width: 132 }, cell: ({ row }) => <span className="tabular font-semibold">{row.original.matterRef}</span> },
    { id: 'patient', accessorKey: 'patientAsOnLoi', header: 'Patient (as on LOI)', meta: { priority: 1 }, cell: ({ row }) => <span className="block truncate" title={row.original.patientAsOnLoi}>{row.original.patientAsOnLoi}</span> },
    { id: 'status', accessorKey: 'status', header: 'Where it is', enableSorting: false, meta: { priority: 2, width: 190 }, cell: ({ row }) => <StatusChip status={row.original.status} size="sm" /> },
    { id: 'confirmed', accessorKey: 'confirmed', header: 'Confirmed', enableSorting: false, meta: { priority: 1, width: 104 }, cell: ({ row }) => <span className={row.original.confirmed ? '' : 'font-medium text-beam-warning'}>{confirmedWord(row.original.confirmed)}</span> },
    { id: 'consent', accessorKey: 'consent', header: 'Consent', enableSorting: false, meta: { priority: 2, width: 88 }, cell: ({ row }) => <span className={row.original.consent === 'none' ? 'text-beam-text-muted' : ''}>{CONSENT_WORD[row.original.consent]}</span> },
    { id: 'questionnaire', accessorKey: 'questionnaire', header: 'Questionnaire', enableSorting: false, meta: { priority: 2, width: 120 }, cell: ({ row }) => <span className={row.original.questionnaire === 'none' ? 'text-beam-text-muted' : ''}>{QUESTIONNAIRE_WORD[row.original.questionnaire]}</span> },
    { id: 'mark', header: 'Mark', enableSorting: false, meta: { priority: 2, width: 142 }, cell: ({ row }) => <Words row={row.original} /> },
    { id: 'open', header: () => <span className="sr-only">Open</span>, enableSorting: false, meta: { priority: 1, width: 72, align: 'right' }, cell: ({ row }) => <Link to={hubHref(row.original.matterRef)} aria-label={`Open ${row.original.matterRef}`} className={buttonVariants({ variant: 'outline', size: 'sm' }) + ' h-7 px-2 text-xs'} onClick={(e) => e.stopPropagation()}>Open</Link> },
  ];
}

function BookingCard({ row }: { row: ClinicRow }) {
  return (
    <article data-slot="booking-card" className="flex flex-col gap-1 rounded-lg border border-beam-line bg-beam-paper p-3 text-sm">
      <span className="flex items-baseline justify-between gap-2">
        <span className="font-semibold">{dayWords(row.startsAt)}</span>
        <span className="tabular whitespace-nowrap text-xs text-beam-text-muted">{timeOf(row.startsAt)}</span>
      </span>
      <span className="truncate text-xs text-beam-text-muted" title={row.clinic.name}>{row.clinic.name}</span>
      <span className="flex items-baseline justify-between gap-2"><span className="truncate">{row.patientAsOnLoi}</span><span className="tabular whitespace-nowrap text-xs font-semibold">{row.matterRef}</span></span>
      <span className={`text-xs ${row.confirmed ? 'text-beam-text-muted' : 'font-medium text-beam-warning'}`}>{confirmedWord(row.confirmed)}</span>
      <span className="text-xs text-beam-text-muted">Consent {CONSENT_WORD[row.consent]}, questionnaire {QUESTIONNAIRE_WORD[row.questionnaire]}</span>
      {markWords(row).length > 0 ? <span className="text-xs"><Words row={row} /></span> : null}
      <Link to={hubHref(row.matterRef)} aria-label={`Open ${row.matterRef}, ${timeOf(row.startsAt)}`} className={buttonVariants({ variant: 'outline', size: 'lg' }) + ' mt-1 h-11 w-full'}>Open</Link>
    </article>
  );
}

export function BookingsTab({ slug, mine, from, onMove }: { slug: string; mine: boolean; from: string | null; onMove: (from: string | null) => void }) {
  const phone = useIsPhone();
  const online = useOnline();
  const bookings = useDoctorBookings(slug, from);
  const rows = bookings.data?.rows ?? [];
  const cols = useMemo(() => columns(), []);
  const w = bookingsWindow(from);
  const label = `${longDate(w.start.toISOString())} to ${longDate(addDays(w.start, 6).toISOString())}`;
  const move = (n: number) => {
    const next = addDays(w.start, n);
    const key = weekKey(next);
    onMove(isToday(key) ? null : key);
    say(`Bookings from ${longDate(next.toISOString())}`);
  };
  const size = phone ? 'lg' : 'sm';
  const bar = (
    <div data-slot="bookings-bar" className="flex flex-wrap items-center gap-2">
      <Button variant="outline" size={size} className={phone ? 'h-11' : undefined} aria-label={`Previous week, from ${longDate(addDays(w.start, -7).toISOString())}`} onClick={() => move(-7)}><ChevronLeftIcon aria-hidden="true" className="size-4" />Previous</Button>
      <span className="font-heading text-[18px] font-medium" aria-live="polite" data-slot="bookings-range">{label}</span>
      <Button variant="outline" size={size} className={phone ? 'h-11' : undefined} disabled={from === null} onClick={() => { onMove(null); say('Bookings from today'); }}>Today</Button>
      <Button variant="outline" size={size} className={phone ? 'h-11' : undefined} aria-label={`Next week, from ${longDate(addDays(w.start, 7).toISOString())}`} onClick={() => move(7)}>Next<ChevronRightIcon aria-hidden="true" className="size-4" /></Button>
    </div>
  );

  if (bookings.isError) {
    return (
      <div className="flex flex-col gap-3">
        {bar}
        <ErrorPanel message={mine ? 'Your bookings could not be loaded.' : 'The bookings could not be loaded.'} onRetry={() => void bookings.refetch()} offline={!online} />
      </div>
    );
  }
  const empty = (
    <EmptyState
      title={from === null ? (mine ? 'No bookings in the next seven days.' : 'No bookings in the next seven days.') : `No bookings from ${longDate(w.start.toISOString())}.`}
      description="Appointments are booked from the case hub or the clinic and appear here for the doctor they are assigned to."
      action={from !== null ? <Button variant="link" className="h-auto p-0" onClick={() => onMove(null)}>Back to today</Button> : undefined}
    />
  );
  return (
    <div className="flex flex-col gap-3" data-slot="bookings">
      {bar}
      <p className="text-xs text-beam-text-muted">Seven days from the first date, today first, in time order. Booking, confirming and marking attendance stay on the clinic and the case hub.</p>
      {phone ? (
        <div data-slot="booking-card-list" className="flex flex-col gap-3">
          {bookings.isPending ? [0, 1, 2].map((i) => <Skeleton key={i} className="h-36 rounded-lg" aria-hidden="true" />) : rows.length === 0 ? empty : rows.map((r) => <BookingCard key={r.apptId + r.matterRef} row={r} />)}
        </div>
      ) : (
        <QueueTable
          columns={cols}
          data={rows}
          sorting={[]}
          onSortingChange={() => undefined}
          getRowId={(r) => `${r.matterRef}:${r.apptId}`}
          rowHref={(r) => hubHref(r.matterRef)}
          caption={`${mine ? 'My bookings' : 'Bookings'} ${label}: ${pluralise(rows.length, 'appointment')}, in time order`}
          state={bookings.isPending ? 'loading' : 'ready'}
          empty={empty}
        />
      )}
      <Legend>
        <LegendItem term={<span className="font-medium">Confirmed</span>}>the patient has confirmed the appointment; "Unconfirmed" in amber until they do.</LegendItem>
        <LegendItem term={<span className="font-medium">Consent, Questionnaire</span>}>returned or complete before the day, in clinic on the day, or none yet; as on the clinic.</LegendItem>
        <LegendItem term={<span className="font-medium">Mark</span>}>attendance once marked, and the case's flags (Urgent, Prior DNA).</LegendItem>
      </Legend>
    </div>
  );
}
